import { useQuery } from "@tanstack/react-query";
import {
  addBusinessDays,
  addDays,
  format,
  isAfter,
  isSameDay,
  isToday,
  startOfDay,
  startOfTomorrow,
} from "date-fns";
import { useEffect, useMemo, useState } from "react";
import { getAvailableDates } from "../../services";
import ScheduleButton from "../schedule-one-on-one/schedule-button.component";
import ScheduleFooter from "../schedule-one-on-one/schedule-footer.component";
import {
  DateWithSlots,
  Meeting,
  scheduleValidation,
} from "../schedule-one-on-one/schedule-one-on-one.constants";
import AvailableDates from "./available-dates/available-dates.component";
import AvailableTimeSlots from "./available-time-slots/available-time-slots.component";
import ContactForm from "./contact-form/contact-form.component";

interface Props {
  upcomingMeeting?: Meeting;
  scheduleError?: boolean;
  sessionPoints?: number;
  onConfirmSchedule?: (meeting: Partial<Meeting>) => void;
  onPurchaseSessionPoints?: () => void;
}

export default function ScheduleForm({
  upcomingMeeting,
  scheduleError,
  sessionPoints,
  onConfirmSchedule,
  onPurchaseSessionPoints,
}: Props) {
  const [start, setStart] = useState<Date>(
    upcomingMeeting?.start || startOfTomorrow()
  );
  const [selectedDate, setSelectedDate] = useState<DateWithSlots>();
  const [selectedTime, setSelectedTime] = useState<string>();
  const [location, setLocation] = useState<string>(upcomingMeeting?.location);
  const [description, setDescription] = useState<string>(
    upcomingMeeting?.description
  );

  const {
    data: datesWithSlots,
    isLoading: isLoadingDatesWithSlots,
    error: errorDatesWithSlots,
    refetch: refetchDatesWithSlots,
  } = useQuery({
    queryKey: ["available-dates", start],
    queryFn: () => getAvailableDates(start),
    select: (data) => {
      const result: DateWithSlots[] = [];
      const now = new Date();

      const uniqueDays = new Set(
        data.map((str) => startOfDay(new Date(str)).toISOString())
      );

      uniqueDays.forEach((day) => {
        const date = new Date(day);
        result.push({
          value: date,
          slots: data.filter((str) => {
            const slot = new Date(str);
            return (
              isAfter(slot, now) && isSameDay(slot, date) && !isToday(slot)
            );
          }),
        });
      });

      return result;
    },
  });

  useEffect(() => {
    if (!start) setStart(startOfTomorrow());

    let temp = start;
    if (!isSameDay(addBusinessDays(addDays(temp, -1), 1), temp)) {
      temp = addBusinessDays(temp, 1);
    }

    let end = addBusinessDays(temp, 2);
    while (end.getMonth() !== temp.getMonth()) {
      temp = addBusinessDays(temp, -1);
      end = addBusinessDays(end, -1);
    }

    if (!isSameDay(temp, start)) {
      setStart(temp);
    }
  }, [start]);

  useEffect(() => {
    if (datesWithSlots?.length && !selectedDate) {
      const found = datesWithSlots.find((d) => isSameDay(d.value, start));
      if (found) setSelectedDate(found);
    }
  }, [datesWithSlots, selectedDate, start]);

  const handleDateChange = (dateWithSlots: DateWithSlots) => {
    setSelectedDate(dateWithSlots);
    setSelectedTime(null);
  };

  const handleStartDateChange = (date: Date) => {
    setStart(date);
    if (selectedDate && !isSameDay(date, selectedDate.value)) {
      setSelectedDate(null);
      setSelectedTime(null);
      refetchDatesWithSlots();
    }
  };

  const isConfirmScheduleEnabled = useMemo(() => {
    return !!selectedTime && location && description?.length >= 2;
  }, [selectedTime, location, description]);

  const confirmScheduleTooltip = useMemo(() => {
    if (!selectedDate) return scheduleValidation.dateInvalid;
    if (!selectedTime) return scheduleValidation.timeInvalid;
    if (!location) return scheduleValidation.locationInvalid;
    if (description?.length < 2) return scheduleValidation.descriptionInvalid;
    return null;
  }, [selectedDate, selectedTime, location, description]);

  const handleConfirmSchedule = () => {
    if (isConfirmScheduleEnabled && onConfirmSchedule) {
      const start = new Date(selectedTime);
      const startHour = format(start, "HH:mm");
      onConfirmSchedule({
        start,
        startHour,
        description,
        location,
      });
    }
  };

  const handlePurchaseClick = () => {
    onPurchaseSessionPoints?.();
  };

  return (
    <>
      <div>
        <AvailableDates
          datesWithSlots={datesWithSlots}
          selected={selectedDate}
          onChange={handleDateChange}
          onDateChange={handleStartDateChange}
          loading={isLoadingDatesWithSlots}
          error={!!errorDatesWithSlots}
        />
        {!isLoadingDatesWithSlots && (
          <>
            <AvailableTimeSlots
              dateWithSlots={selectedDate}
              selected={selectedTime}
              onChange={setSelectedTime}
              error={scheduleError}
            />
            <ContactForm
              location={location}
              onChangeLocation={setLocation}
              description={description}
              onChangeDescription={setDescription}
            />
          </>
        )}
      </div>

      <ScheduleFooter
        sessionPoints={sessionPoints}
        onPurchaseSessionPoints={handlePurchaseClick}
      />

      <ScheduleButton
        onClick={handleConfirmSchedule}
        disabled={!isConfirmScheduleEnabled}
        tooltip={confirmScheduleTooltip}
      />
    </>
  );
}
